// frontend/src/pages/shops/BusinessReviews.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SidebarLayout from "./SidebarLayout";
import axios from "axios";
import "./shopStyles.css";

function BusinessReviews() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({ total: 0, average: 0, breakdown: [] });
  const [filter, setFilter] = useState("all"); // all, 5, 4, 3, 2, 1
  const [responseText, setResponseText] = useState({});
  const [respondingTo, setRespondingTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("all");

  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (locations.length > 0) {
      fetchReviews();
    }
  }, [selectedLocation, filter, locations]);

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/shops/my-shops`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setLocations(response.data.shops || []);
    } catch (err) {
    }
  };

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("ogestyle_token");

      // Determine which locations to fetch reviews from
      const shopIds =
        selectedLocation === "all"
          ? locations.map((loc) => loc._id)
          : [selectedLocation];

      // Determine filter parameters
      const params = { limit: 100 };

      // Handle status filter (pending, approved, flagged)
      if (["pending", "approved", "flagged"].includes(filter)) {
        params.status = filter;
      }

      // Handle rating filter (rating-1, rating-2, etc.)
      if (filter.startsWith("rating-")) {
        const rating = filter.split("-")[1];
        params.rating = parseInt(rating);
      }

      // Fetch reviews for each location using AUTHENTICATED endpoint to get ALL reviews (including pending)
      const reviewPromises = shopIds.map((shopId) =>
        axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/reviews/business/shop/${shopId}`,
          {
            params,
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
      );

      const responses = await Promise.all(reviewPromises);
      const allReviews = responses.flatMap((res) => res.data.reviews || []);

      // Sort by date (most recent first)
      allReviews.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setReviews(allReviews);

      // Calculate aggregate stats
      const total = allReviews.length;
      const sum = allReviews.reduce((acc, review) => acc + review.rating, 0);
      const average = total > 0 ? (sum / total).toFixed(1) : 0;

      // Rating breakdown
      const breakdown = [1, 2, 3, 4, 5].map((rating) => ({
        _id: rating,
        count: allReviews.filter((r) => r.rating === rating).length,
      }));

      setStats({ total, average, breakdown });
      setLoading(false);
    } catch (err) {
      setError("Failed to load reviews");
      setLoading(false);
    }
  };

  const handleAddResponse = async (reviewId) => {
    if (!responseText[reviewId] || responseText[reviewId].trim().length === 0) {
      alert("Please enter a response");
      return;
    }

    if (responseText[reviewId].length > 500) {
      alert("Response must be 500 characters or less");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("ogestyle_token");
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/reviews/${reviewId}/response`,
        { responseText: responseText[reviewId] },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setResponseText({ ...responseText, [reviewId]: "" });
      setRespondingTo(null);
      fetchReviews();
      setSubmitting(false);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to add response");
      setSubmitting(false);
    }
  };

  const handleApproveReview = async (reviewId) => {
    if (
      !window.confirm("Approve this review and make it visible to the public?")
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/api/reviews/${reviewId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert("Review approved successfully! The customer has been notified.");
      fetchReviews();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to approve review");
    }
  };

  const renderStars = (rating) => {
    return (
      <div style={{ display: "flex", gap: "2px" }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            style={{
              fontSize: isMobile ? "12px" : "14px",
              color: star <= rating ? "#fbbf24" : "#d1d5db",
            }}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  if (loading && locations.length === 0) {
    return (
      <SidebarLayout>
        <div
          className="reviews-page"
          style={{ padding: isMobile ? "16px" : "24px" }}
        >
          <p>Loading...</p>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div
        className="reviews-page"
        style={{ padding: isMobile ? "16px" : "24px" }}
      >
        {/* Page Header */}
        <div style={{ marginBottom: isMobile ? "16px" : "24px" }}>
          <h1
            style={{
              fontSize: isMobile ? "18px" : "24px",
              fontWeight: "700",
              color: "#111827",
              margin: "0 0 8px 0",
            }}
          >
            ⭐ Reviews
          </h1>
          <p
            style={{
              fontSize: isMobile ? "11px" : "13px",
              color: "#6b7280",
              margin: 0,
            }}
          >
            View and respond to customer reviews across all your locations
          </p>
        </div>

        {/* Filters */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: "12px",
            marginBottom: "20px",
          }}
        >
          {/* Location Filter */}
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            style={{
              padding: "10px 12px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "13px",
              flex: 1,
            }}
          >
            <option value="all">All Locations ({locations.length})</option>
            {locations.map((loc) => (
              <option key={loc._id} value={loc._id}>
                {loc.city && loc.state ? `${loc.city}, ${loc.state}` : loc.name}
              </option>
            ))}
          </select>

          {/* Status Filter - NEW */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              padding: "10px 12px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "13px",
              flex: 1,
            }}
          >
            <option value="all">All Reviews</option>
            <option value="pending">⏳ Pending Approval</option>
            <option value="approved">✅ Approved</option>
            <option value="flagged">🚩 Flagged</option>
          </select>

          {/* Rating Filter */}
          <select
            value={filter.startsWith("rating-") ? filter : "all-ratings"}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              padding: "10px 12px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "13px",
              flex: 1,
            }}
          >
            <option value="all-ratings">All Ratings</option>
            <option value="rating-5">⭐⭐⭐⭐⭐ (5 stars)</option>
            <option value="rating-4">⭐⭐⭐⭐ (4 stars)</option>
            <option value="rating-3">⭐⭐⭐ (3 stars)</option>
            <option value="rating-2">⭐⭐ (2 stars)</option>
            <option value="rating-1">⭐ (1 star)</option>
          </select>
        </div>

        {/* Stats Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
            gap: "12px",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              background: "white",
              padding: isMobile ? "12px" : "16px",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: isMobile ? "20px" : "28px",
                fontWeight: "700",
                color: "#111827",
              }}
            >
              {stats.total}
            </div>
            <div
              style={{ fontSize: isMobile ? "10px" : "12px", color: "#6b7280" }}
            >
              Total Reviews
            </div>
          </div>

          <div
            style={{
              background: "white",
              padding: isMobile ? "12px" : "16px",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: isMobile ? "20px" : "28px",
                fontWeight: "700",
                color: "#fbbf24",
              }}
            >
              {stats.average}
            </div>
            <div
              style={{ fontSize: isMobile ? "10px" : "12px", color: "#6b7280" }}
            >
              Avg Rating
            </div>
          </div>

          <div
            style={{
              background: "white",
              padding: isMobile ? "12px" : "16px",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: isMobile ? "20px" : "28px",
                fontWeight: "700",
                color: "#10b981",
              }}
            >
              {reviews.filter((r) => r.businessResponse?.text).length}
            </div>
            <div
              style={{ fontSize: isMobile ? "10px" : "12px", color: "#6b7280" }}
            >
              Responded
            </div>
          </div>

          <div
            style={{
              background: "white",
              padding: isMobile ? "12px" : "16px",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: isMobile ? "20px" : "28px",
                fontWeight: "700",
                color: "#f59e0b",
              }}
            >
              {reviews.filter((r) => !r.businessResponse?.text).length}
            </div>
            <div
              style={{ fontSize: isMobile ? "10px" : "12px", color: "#6b7280" }}
            >
              Pending
            </div>
          </div>
        </div>

        {/* Reviews List */}
        {error && (
          <div
            style={{
              padding: "12px",
              background: "#fee2e2",
              color: "#991b1b",
              borderRadius: "8px",
              marginBottom: "16px",
              fontSize: "13px",
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div
            style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}
          >
            Loading reviews...
          </div>
        ) : reviews.length === 0 ? (
          <div
            style={{
              background: "white",
              padding: isMobile ? "32px 16px" : "48px 24px",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: isMobile ? "36px" : "48px",
                marginBottom: "16px",
              }}
            >
              📝
            </div>
            <h3
              style={{
                fontSize: isMobile ? "16px" : "18px",
                fontWeight: "600",
                color: "#111827",
                marginBottom: "8px",
              }}
            >
              No reviews yet
            </h3>
            <p style={{ fontSize: "13px", color: "#6b7280", margin: 0 }}>
              {filter === "all"
                ? "Customer reviews will appear here once bookings are completed"
                : `No ${filter}-star reviews found`}
            </p>
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {reviews.map((review) => (
              <div
                key={review._id}
                style={{
                  background: !review.businessResponse?.text
                    ? "#fffbeb"
                    : "white",
                  padding: isMobile ? "16px" : "20px",
                  borderRadius: "12px",
                  border: "1px solid #e5e7eb",
                }}
              >
                {/* Review Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "12px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: isMobile ? "12px" : "14px",
                        fontWeight: "600",
                        color: "#111827",
                        marginBottom: "4px",
                      }}
                    >
                      {/* First name only. A full surname is more personal data than a
                          public review needs to carry, so no last initial either. */}
                      {review.customerName ||
                        review.customerId?.firstName?.trim() ||
                        review.bookingId?.customerName?.trim()?.split(" ")?.[0] ||
                        "Anonymous"}
                    </div>
                    {renderStars(review.rating)}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        fontSize: isMobile ? "10px" : "11px",
                        color: "#9ca3af",
                        marginBottom: "4px",
                      }}
                    >
                      {new Date(review.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </div>
                    {/* Show location name - ALWAYS */}
                    <div
                      style={{
                        fontSize: "10px",
                        color: "#6b7280",
                        background: "#f3f4f6",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        display: "inline-block",
                        marginBottom: "4px",
                      }}
                    >
                      📍 {review.shopId?.name || "Location"}
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                {review.status === "pending" && (
                  <div
                    style={{
                      display: "inline-block",
                      padding: "4px 10px",
                      background: "#fef3c7",
                      color: "#92400e",
                      fontSize: "11px",
                      fontWeight: "600",
                      borderRadius: "12px",
                      marginBottom: "8px",
                    }}
                  >
                    ⏳ Pending Approval
                  </div>
                )}
                {review.status === "approved" && (
                  <div
                    style={{
                      display: "inline-block",
                      padding: "4px 10px",
                      background: "#d1fae5",
                      color: "#065f46",
                      fontSize: "11px",
                      fontWeight: "600",
                      borderRadius: "12px",
                      marginBottom: "8px",
                    }}
                  >
                    ✅ Published
                  </div>
                )}
                {review.status === "flagged" && (
                  <div
                    style={{
                      display: "inline-block",
                      padding: "4px 10px",
                      background: "#fee2e2",
                      color: "#991b1b",
                      fontSize: "11px",
                      fontWeight: "600",
                      borderRadius: "12px",
                      marginBottom: "8px",
                    }}
                  >
                    🚩 Flagged for Review
                  </div>
                )}

                {/* Review Text */}
                <p
                  style={{
                    margin: "0 0 12px 0",
                    fontSize: isMobile ? "12px" : "13px",
                    color: "#4b5563",
                    lineHeight: 1.6,
                  }}
                >
                  {review.reviewText || "(No comment)"}
                </p>

                {/* Approve Button for Pending Reviews */}
                {review.status === "pending" && (
                  <button
                    onClick={() => handleApproveReview(review._id)}
                    style={{
                      padding: "8px 16px",
                      background: "#10b981",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      marginBottom: "12px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    ✅ Approve & Publish Review
                  </button>
                )}

                {/* Awaiting Response Badge (only for approved reviews without response) */}
                {review.status === "approved" &&
                  !review.businessResponse?.text && (
                    <div
                      style={{
                        display: "inline-block",
                        padding: "4px 10px",
                        background: "#dbeafe",
                        color: "#1e40af",
                        fontSize: "11px",
                        fontWeight: "600",
                        borderRadius: "12px",
                        marginBottom: "12px",
                      }}
                    >
                      💬 Ready to Respond
                    </div>
                  )}

                {/* Business Response */}
                {review.businessResponse?.text && (
                  <div
                    style={{
                      background: "#f0f9ff",
                      padding: "12px",
                      borderRadius: "8px",
                      borderLeft: "3px solid #3b82f6",
                      marginTop: "12px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#1e40af",
                        marginBottom: "6px",
                      }}
                    >
                      Response from Business
                    </div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: isMobile ? "11px" : "12px",
                        color: "#374151",
                        lineHeight: 1.5,
                      }}
                    >
                      {review.businessResponse.text}
                    </p>
                    <div
                      style={{
                        fontSize: "10px",
                        color: "#6b7280",
                        marginTop: "6px",
                      }}
                    >
                      {new Date(
                        review.businessResponse.respondedAt,
                      ).toLocaleDateString()}
                    </div>
                  </div>
                )}

                {/* Response Form - only show for approved reviews */}
                {review.status === "approved" &&
                  !review.businessResponse?.text && (
                    <div style={{ marginTop: "12px" }}>
                      {respondingTo === review._id ? (
                        <div>
                          <textarea
                            value={responseText[review._id] || ""}
                            onChange={(e) =>
                              setResponseText({
                                ...responseText,
                                [review._id]: e.target.value,
                              })
                            }
                            placeholder="Write your response..."
                            style={{
                              width: "100%",
                              padding: "10px",
                              border: "1px solid #d1d5db",
                              borderRadius: "8px",
                              fontSize: "13px",
                              minHeight: "80px",
                              resize: "vertical",
                              fontFamily: "inherit",
                            }}
                            maxLength={500}
                          />
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginTop: "8px",
                            }}
                          >
                            <span
                              style={{ fontSize: "11px", color: "#6b7280" }}
                            >
                              {(responseText[review._id] || "").length}/500
                              characters
                            </span>
                            <div style={{ display: "flex", gap: "8px" }}>
                              <button
                                onClick={() => {
                                  setRespondingTo(null);
                                  setResponseText({
                                    ...responseText,
                                    [review._id]: "",
                                  });
                                }}
                                style={{
                                  padding: "8px 16px",
                                  background: "white",
                                  color: "#6b7280",
                                  border: "1px solid #d1d5db",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleAddResponse(review._id)}
                                disabled={submitting}
                                style={{
                                  padding: "8px 16px",
                                  background: "#3b82f6",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  cursor: submitting
                                    ? "not-allowed"
                                    : "pointer",
                                  opacity: submitting ? 0.6 : 1,
                                }}
                              >
                                {submitting ? "Posting..." : "Post Response"}
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setRespondingTo(review._id)}
                          style={{
                            padding: "8px 16px",
                            background: "#3b82f6",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          💬 Respond to Review
                        </button>
                      )}
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}
      </div>
    </SidebarLayout>
  );
}

export default BusinessReviews;
